<template>
    <div>
      <div class="chart_holder" v-if="chartData && chartData.length">
        <plotly :data="chartData" :layout="chartLayout" :config="chartConfig"/>
      </div>
    </div>
  </template>
  
  <script>
  import { Plotly } from 'vue-plotly'
  import defaultLayout from '@/components/helpers/plotly_style'
  import ReportDataHelper from '@/components/helpers/ReportDataHelper'
  import moment from 'moment'
  
  export default {
    name: 'PieChartPlotly',
    components: {
      Plotly
    },
    data() {
      return {
        // Auto-populated from the parent. You do not need to pass these in the HTML.
        statsData: null,
        liveData: null,
        related_ids: null,
        reportParams: null,
        report: null
      }
    },
    props: {
      /*
        "stats" or "live"
        Controls whether we read from statsData or liveData.
      */
      data_source: {
        type: String,
        default: 'stats'
      },
      /*
        A single statistic or an array. If multiple, we'll just use the first for the pie.
      */
      statistics: {
        type: [Array, String],
        default: () => []
      },
      /*
        Aggregation method: sum, max, min, count, etc. 
        For stats data, we combine all date-values per device. 
        For live, usually we just read the single numeric field.
      */
      aggregration_mode: {
        type: String,
        default: 'sum'
      },
      /*
        If true, skip devices where total is near zero.
      */
      hideZeroValues: {
        type: Boolean,
        default: false
      },
      /*
        If true, highlight the largest slice with a different color.
      */
      highlightMax: {
        type: Boolean,
        default: false
      },
      /*
        For stats mode, we sum across all date labels. 
        This multiplier can convert units if needed (e.g. L → kL).
      */
      value_multiple: {
        type: Number,
        default: 1
      },
      /*
        For live data, we read from liveData[this.data_key].
      */
      data_key: {
        type: String,
        default: 'devices'
      },
      /*
        The label_field for live data. e.g. "device_name".
        For stats, we often just use the device ID => related_ids map to get a label.
      */
      label_field: {
        type: String,
        default: 'device_name'
      },
      /*
        Maximum number of slices to display. 
        Remaining devices are combined into an “Others” slice.
      */
      maxSlices: {
        type: Number,
        default: 10
      },
      /*
        If needed, fallback for missing data. 
      */
      null_values: {
        default: 0
      },
      /*
        The Plotly layout (e.g. { title: 'Fuel Distribution' }).
      */
      layout: {
        type: Object,
        default: () => ({})
      },
      /*
        The Plotly config (e.g. { responsive: true }).
      */
      config: {
        type: Object,
        default: () => ({ responsive: true })
      }
    },
    methods: {
      async getRequiredStats() {
        if (this.data_source === 'stats') {
          let stats = []
          if (this.statistics) {
            let newStats
            if (this.statistics instanceof Promise) {
              newStats = await this.statistics
            } else {
              newStats = this.statistics
            }
            // Flatten
            stats = stats.concat(newStats).flat()
          }
          return stats
        }
        return []
      },
  
      async getRequiredDeviceProps() {
        if (this.data_source === 'live') {
          let fields = []
          if (this.statistics) {
            let newStats
            if (this.statistics instanceof Promise) {
              newStats = await this.statistics
            } else {
              newStats = this.statistics
            }
            fields = fields.concat(newStats).flat()
          }
          if (this.label_field) {
            fields.push(this.label_field)
          }
          return fields
        }
        return []
      },
  
      // For stats data, aggregator across all date labels => device total
      _aggregateStats(statName) {
        if (!this.statsData || !this.statsData.stats || !this.statsData.stats[statName]) {
          return {}
        }
        const deviceDict = this.statsData.stats[statName]
        const deviceTotals = {}
        this.statsData.labels.forEach(lbl => {
          Object.keys(deviceDict).forEach(deviceId => {
            const rawVal = deviceDict[deviceId][lbl] ?? this.null_values
            const val = rawVal * this.value_multiple
            if (!(deviceId in deviceTotals)) {
              deviceTotals[deviceId] = 0
            }
            switch (this.aggregration_mode) {
              case 'sum':
                deviceTotals[deviceId] += val
                break
              case 'max':
                deviceTotals[deviceId] = Math.max(deviceTotals[deviceId], val)
                break
              case 'min':
                deviceTotals[deviceId] = Math.min(deviceTotals[deviceId], val)
                break
              case 'count':
                deviceTotals[deviceId] += (val !== this.null_values ? 1 : 0)
                break
              default:
                // fallback to sum
                deviceTotals[deviceId] += val
                break
            }
          })
        })
        return deviceTotals
      },
  
      // For live data, we typically read a single field from each device object
      _collectLiveValues(statName) {
        if (!this.liveData || !this.liveData[this.data_key]) {
          return {}
        }
        const devicesObj = this.liveData[this.data_key]
        const deviceTotals = {}
        Object.keys(devicesObj).forEach(devKey => {
          const devObj = devicesObj[devKey]
          // e.g. devObj.hours_tracked
          let rawVal = ReportDataHelper.getNestedField(devObj, statName, this.null_values)
          let val = rawVal * this.value_multiple
          switch (this.aggregration_mode) {
            case 'sum':
            case 'max':
            case 'min':
            case 'count':
              // for single-value: sum or count => same as sum once
              // max => val, min => val (since we only have one data point)
              // we can unify logic, but let's do sum
              deviceTotals[devKey] = val
              break
            default:
              deviceTotals[devKey] = val
              break
          }
        })
        return deviceTotals
      }
    },
    computed: {
      chartData() {
        // 1) pick first statistic if array
        let statArr = Array.isArray(this.statistics) ? this.statistics : [this.statistics]
        if (!statArr.length) return []
        const primaryStat = statArr[0]
  
        // 2) Build a map: deviceId -> numericValue
        let deviceMap = {}
        if (this.data_source === 'stats') {
          deviceMap = this._aggregateStats(primaryStat)
        } else {
          deviceMap = this._collectLiveValues(primaryStat)
        }
  
        // 3) Convert to deviceLabel -> sum
        let labelValuePairs = []
        Object.keys(deviceMap).forEach(deviceId => {
          let numericVal = deviceMap[deviceId]
          if (Math.abs(numericVal) < 1e-9 && this.hideZeroValues) {
            // skip zero
            return
          }
          // Build label from deviceId or device_name
          let deviceLabel = deviceId
          if (this.data_source === 'stats' && this.related_ids && this.related_ids[deviceId]) {
            deviceLabel = this.related_ids[deviceId]
          } else if (
            this.data_source === 'live' &&
            this.liveData &&
            this.liveData[this.data_key][deviceId]
          ) {
            // e.g. devObj.device_name
            deviceLabel = ReportDataHelper.getNestedField(
              this.liveData[this.data_key][deviceId],
              this.label_field,
              deviceId
            )
          }
          labelValuePairs.push({ label: deviceLabel, value: numericVal })
        })
  
        // 4) Sort descending by value
        labelValuePairs.sort((a, b) => b.value - a.value)
  
        // 5) If more than maxSlices, combine remainder into "Others"
        if (labelValuePairs.length > this.maxSlices) {
          const topN = labelValuePairs.slice(0, this.maxSlices)
          const remaining = labelValuePairs.slice(this.maxSlices)
          const othersSum = remaining.reduce((acc, x) => acc + x.value, 0)
          topN.push({ label: 'Others', value: othersSum })
          labelValuePairs = topN
        }
  
        // 6) Build the single pie trace
        let labels = labelValuePairs.map(x => x.label)
        let values = labelValuePairs.map(x => x.value)
  
        let trace = {
          type: 'pie',
          labels,
          values,
          textinfo: 'label+percent', // or 'value+percent'
          hoverinfo: 'label+value+percent'
        }
  
        // 7) If highlightMax = true, color the largest slice differently
        if (this.highlightMax && values.length > 0) {
          let maxIndex = 0
          let maxVal = Number.NEGATIVE_INFINITY
          values.forEach((v, i) => {
            if (v > maxVal) {
              maxVal = v
              maxIndex = i
            }
          })
          const defaultColors = Array(values.length).fill('#b0b0b0')
          defaultColors[maxIndex] = '#1f77b4' // highlight
          trace.marker = { colors: defaultColors }
        }
  
        return [trace]
      },
      chartLayout() {
        // Merge with default layout
        let newLayout = {
          ...defaultLayout,
          ...this.layout
        }
        // Pie charts typically don’t need xaxis/yaxis config, 
        // but you can add them if you want. 
        return newLayout
      },
      chartConfig() {
        return this.config
      }
    }
  }
  </script>
  
  <style scoped>
  .chart_holder {
    width: 100%;
    height: 100%;
    min-height: 300px;
  }
  </style>
  